import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "react-router";

/** STYLE 7. "link" and "text" are the same text action; "block" and "sso" are the full-width sign-in buttons. */
export type ButtonVariant = "primary" | "secondary" | "danger" | "danger-fill" | "small" | "link" | "text" | "text-danger" | "block" | "sso";

// The design system's Button (primary, secondary); the app adds the kinds the
// system does not have yet (danger, small, text, block, sso) on top of its .btn.
const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "btn--primary",
  secondary: "btn--secondary",
  small: "btn--secondary btn--small",
  block: "btn--primary btn--block",
  sso: "btn--secondary btn--block",
  danger: "btn--danger",
  "danger-fill": "btn--danger-fill",
  link: "btn--text",
  text: "btn--text",
  "text-danger": "btn--text-danger",
};

const classFor = (variant: ButtonVariant, extra?: string) =>
  [
    "btn",
    VARIANT_CLASS[variant],
    extra ?? "",
  ]
    .filter(Boolean)
    .join(" ");

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "disabled"> & {
  variant?: ButtonVariant;
  /** Shows "busy" text and blocks clicks while a request runs. */
  busy?: boolean;
  busyLabel?: string;
  /**
   * Disables the button (aria-disabled, stays focusable). Pass `disabledReason`
   * so the reason is shown below it: never hide why an action is unavailable.
   */
  disabled?: boolean;
  disabledReason?: ReactNode;
};

export function Button({
  variant = "primary",
  busy = false,
  busyLabel,
  disabled = false,
  disabledReason,
  className,
  type = "button",
  onClick,
  children,
  id,
  ...rest
}: ButtonProps) {
  const blocked = disabled || busy;
  const hintId = disabledReason && disabled && id ? `${id}-hint` : undefined;
  const button = (
    <button
      {...rest}
      id={id}
      type={type}
      className={classFor(variant, className)}
      aria-disabled={blocked || undefined}
      aria-describedby={hintId ?? rest["aria-describedby"]}
      onClick={(e) => {
        if (blocked) {
          e.preventDefault();
          return;
        }
        onClick?.(e);
      }}
    >
      {busy ? (busyLabel ?? "Please wait…") : children}
    </button>
  );
  if (disabled && disabledReason) {
    return (
      <span>
        {button}
        <span className="btn-hint" id={hintId}>
          {disabledReason}
        </span>
      </span>
    );
  }
  return button;
}

/** A react-router link that looks like a button. */
export function LinkButton(props: LinkProps & { variant?: ButtonVariant }) {
  return <ButtonLink {...props} />;
}

/** A react-router link that looks like a button (older name, same component). */
export function ButtonLink({ variant = "primary", className, ...rest }: LinkProps & { variant?: ButtonVariant }) {
  return <Link {...rest} className={classFor(variant, className)} />;
}
